import React, { useEffect, useMemo, useState } from 'react'
import whatsappApi from './whatsappApi'
import myToast from '../../Utils/MyToast'
import {
  filterTemplatesByService,
  formatTemplateName,
  formatVariableLabel,
  getTemplateVariables,
} from './waTemplateUtils'
import WhatsAppTemplateVerifyReportDialog from './WhatsAppTemplateVerifyReportDialog'

const WA = '#25D366'
const WA_DARK = '#128C7E'

const STATUS_STYLE = {
  approved: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
  pending: { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
  in_preview: { bg: '#dbeafe', color: '#1d4ed8', label: 'In Preview' },
  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  paused: { bg: '#e5e7eb', color: '#374151', label: 'Paused' },
}

// Created By Cursor — local pending + already sent to Meta = In Preview
function getTemplateDisplayStatus(tpl) {
  const status = String(tpl?.status || '').toLowerCase()
  const metaId = String(tpl?.meta_template_id || '').trim()
  if (status === 'pending' && metaId) {
    return 'in_preview'
  }
  return status || 'pending'
}

// Created By Cursor
const WhatsAppTemplateManager = ({ connected, messageServices = [] }) => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedServiceCode, setSelectedServiceCode] = useState('all')
  const [sendingId, setSendingId] = useState(null)
  const [verifyingId, setVerifyingId] = useState(null)
  const [verifyReport, setVerifyReport] = useState(null)

  const services = useMemo(
    () => (Array.isArray(messageServices) ? messageServices : []),
    [messageServices]
  )

  const selectedService = useMemo(() => {
    if (selectedServiceCode === 'all') return 'all'
    return (
      services.find(
        (s) => String(s.service_code || s.id) === String(selectedServiceCode)
      ) || 'all'
    )
  }, [services, selectedServiceCode])

  const filteredTemplates = useMemo(
    () => filterTemplatesByService(templates, selectedService),
    [templates, selectedService]
  )

  const load = async () => {
    setLoading(true)
    try {
      const res = await whatsappApi.getTemplates()
      if (res.api_status === 'OK') {
        setTemplates(res.data.templates || [])
      } else {
        myToast.notify_error(res.api_remark || 'Failed to load templates')
      }
    } catch (e) {
      myToast.notify_error('Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  // Created By Cursor
  const sendToMeta = async (tpl) => {
    if (!connected) {
      myToast.notify_error('Connect WhatsApp first')
      return
    }
    if (!tpl?.id) return
    if (!window.confirm(`Send "${formatTemplateName(tpl.name)}" to Meta for approval?`)) {
      return
    }
    setSendingId(tpl.id)
    try {
      const res = await whatsappApi.submitTemplateToMeta(tpl.id)
      if (res.api_status === 'OK') {
        myToast.notify_success(
          res.data?.already_submitted
            ? 'Template already submitted to Meta'
            : 'Template sent to Meta'
        )
        const updated = res.data?.template
        if (updated) {
          setTemplates((prev) =>
            prev.map((t) => (String(t.id) === String(updated.id) ? { ...t, ...updated } : t))
          )
        } else {
          load()
        }
      } else {
        myToast.notify_error(res.api_remark || 'Send to Meta failed')
      }
    } catch (e) {
      myToast.notify_error(e.message || 'Send to Meta failed')
    } finally {
      setSendingId(null)
    }
  }

  // Created By Cursor — Rec 3 manual: pull Meta body/param count into DB for approved templates
  const verifyFromMeta = async (tpl) => {
    if (!connected) {
      myToast.notify_error('Connect WhatsApp first')
      return
    }
    if (!tpl?.id) return
    setVerifyingId(tpl.id)
    try {
      const res = await whatsappApi.verifyTemplateFromMeta(tpl.id, true)
      if (res.api_status === 'OK') {
        const data = res.data || {}
        setVerifyReport({
          report: data.report || { rows: [], all_match: !!data.matched },
          matched: !!data.matched,
          synced: !!data.synced,
          message: data.message || '',
        })
        const updated = data.template
        if (updated) {
          setTemplates((prev) =>
            prev.map((t) => (String(t.id) === String(updated.id) ? { ...t, ...updated } : t))
          )
        } else {
          load()
        }
      } else {
        myToast.notify_error(res.api_remark || 'Verify from Meta failed')
      }
    } catch (e) {
      myToast.notify_error(e.message || 'Verify from Meta failed')
    } finally {
      setVerifyingId(null)
    }
  }

  return (
    <div style={{ padding: '4px 0' }}>
      {verifyReport ? (
        <WhatsAppTemplateVerifyReportDialog
          report={verifyReport.report}
          matched={verifyReport.matched}
          synced={verifyReport.synced}
          message={verifyReport.message}
          onClose={() => setVerifyReport(null)}
        />
      ) : null}

      {!connected && (
        <div
          className="alert alert-warning"
          style={{ maxWidth: 640, borderRadius: 10, fontSize: 13 }}
        >
          Connect WhatsApp first to use templates for messaging.
        </div>
      )}

      <div
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          background: '#fff',
          padding: '12px 14px',
          marginBottom: 14,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#64748b',
            marginBottom: 8,
          }}
        >
          Filter by service
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <ServiceFilterChip
            label="All templates"
            count={templates.length}
            active={selectedServiceCode === 'all'}
            onClick={() => setSelectedServiceCode('all')}
          />
          {services.map((svc) => {
            const code = String(svc.service_code || svc.id || '')
            const name = svc.service_name || svc.service_code || 'Service'
            const count = filterTemplatesByService(templates, svc).length
            return (
              <ServiceFilterChip
                key={code}
                label={name}
                count={count}
                icon={svc.icon}
                active={selectedServiceCode === code}
                onClick={() => setSelectedServiceCode(code)}
              />
            )
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 24, color: '#64748b', fontWeight: 600 }}>Loading…</div>
      ) : filteredTemplates.length === 0 ? (
        <div
          style={{
            border: '1px dashed #cbd5e1',
            borderRadius: 12,
            background: '#f8fafc',
            padding: '36px 16px',
            textAlign: 'center',
            color: '#94a3b8',
            fontWeight: 600,
          }}
        >
          {selectedServiceCode === 'all'
            ? 'No templates yet'
            : 'No templates for this service'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredTemplates.map((t) => {
            const displayStatus = getTemplateDisplayStatus(t)
            const st = STATUS_STYLE[displayStatus] || STATUS_STYLE.pending
            const metaId = String(t.meta_template_id || '').trim()
            // Only local drafts (pending, not yet on Meta) can be sent
            const canSendToMeta = displayStatus === 'pending' && !metaId
            const canVerifyFromMeta = displayStatus === 'approved'
            const busy = String(sendingId) === String(t.id)
            const verifying = String(verifyingId) === String(t.id)
            return (
              <div
                key={t.id}
                style={{
                  border: '1px solid #cbd5e1',
                  borderRadius: 12,
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '12px 14px',
                    background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)',
                    borderBottom: '1px solid #e2e8f0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>
                      {formatTemplateName(t.name)}
                    </div>
                    <span
                      style={{
                        background: st.bg,
                        color: st.color,
                        fontWeight: 800,
                        fontSize: 12,
                        padding: '4px 10px',
                        borderRadius: 999,
                        textTransform: 'uppercase',
                      }}
                    >
                      {st.label || displayStatus}
                    </span>
                    {t.category ? (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          letterSpacing: '0.03em',
                          textTransform: 'uppercase',
                          color: '#0f766e',
                          background: '#ccfbf1',
                          borderRadius: 999,
                          padding: '4px 10px',
                        }}
                      >
                        {t.category}
                      </span>
                    ) : null}
                    {t.group_name ? (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#475569',
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: 999,
                          padding: '4px 10px',
                        }}
                      >
                        {t.group_name}
                      </span>
                    ) : null}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                  {canSendToMeta && (
                    <button
                      type="button"
                      disabled={!connected || busy || verifying}
                      onClick={() => sendToMeta(t)}
                      style={{
                        border: 'none',
                        background: connected
                          ? `linear-gradient(135deg, ${WA_DARK}, ${WA})`
                          : '#cbd5e1',
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: 13,
                        padding: '8px 14px',
                        borderRadius: 8,
                        cursor: connected && !busy ? 'pointer' : 'not-allowed',
                        opacity: busy ? 0.75 : 1,
                        whiteSpace: 'nowrap',
                        boxShadow: connected
                          ? '0 3px 10px rgba(37, 211, 102, 0.22)'
                          : 'none',
                      }}
                    >
                      {busy ? 'Sending…' : 'Send To Meta'}
                    </button>
                  )}
                  {canVerifyFromMeta && (
                    <button
                      type="button"
                      disabled={!connected || verifying || busy}
                      onClick={() => verifyFromMeta(t)}
                      title="Fetch template from Meta and sync body / param count into DB"
                      style={{
                        border: connected ? `1px solid ${WA_DARK}` : '1px solid #cbd5e1',
                        background: connected ? '#fff' : '#f1f5f9',
                        color: connected ? WA_DARK : '#94a3b8',
                        fontWeight: 800,
                        fontSize: 13,
                        padding: '8px 14px',
                        borderRadius: 8,
                        cursor: connected && !verifying ? 'pointer' : 'not-allowed',
                        opacity: verifying ? 0.75 : 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {verifying ? 'Verifying…' : 'Verify From Meta'}
                    </button>
                  )}
                  </div>
                  {displayStatus === 'in_preview' && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#1d4ed8',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: 8,
                        padding: '7px 12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Awaiting Meta approval
                    </span>
                  )}
                </div>

                <div style={{ padding: '10px 14px 12px' }}>
                  {t.header_text ? (
                    <ContentBlock label="Header" text={t.header_text} tone="header" />
                  ) : null}

                  <ContentBlock
                    label="Body"
                    text={t.body_text || '—'}
                    tone="body"
                  />

                  {t.footer_text ? (
                    <ContentBlock label="Footer" text={t.footer_text} tone="footer" />
                  ) : null}

                  <UsedVariablesBlock template={t} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!loading && selectedServiceCode !== 'all' && filteredTemplates.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
          Showing {filteredTemplates.length} of {templates.length} templates
        </div>
      )}
    </div>
  )
}

// Created By Cursor
function UsedVariablesBlock({ template }) {
  const vars = getTemplateVariables(template)
  if (!vars.length) return null

  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          color: '#64748b',
          marginBottom: 6,
        }}
      >
        Used variables
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {vars.map((v) => (
          <span
            key={`${v.placeholder}-${v.label || v.index}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              fontWeight: 700,
              color: '#0f172a',
              background: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              padding: '5px 10px',
            }}
          >
            <code
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: WA_DARK,
                background: `${WA}22`,
                borderRadius: 4,
                padding: '1px 6px',
              }}
            >
              {v.placeholder}
            </code>
            {v.label ? (
              <span style={{ color: '#334155' }}>{formatVariableLabel(v.label)}</span>
            ) : (
              <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                Variable {v.index}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  )
}

// Created By Cursor
function ContentBlock({ label, text, tone }) {
  const styles =
    tone === 'header'
      ? { bg: '#eff6ff', border: '#93c5fd', label: '#1d4ed8' }
      : tone === 'footer'
      ? { bg: '#f8fafc', border: '#cbd5e1', label: '#475569' }
      : { bg: '#f0fdf4', border: '#86efac', label: WA_DARK }

  return (
    <div
      style={{
        marginBottom: 8,
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        borderRadius: 8,
        padding: '9px 12px',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          color: styles.label,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: '#0f172a',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {text}
      </div>
    </div>
  )
}

// Created By Cursor
function ServiceFilterChip({ label, count, icon, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: active ? `1px solid ${WA_DARK}` : '1px solid #cbd5e1',
        background: active ? `linear-gradient(135deg, ${WA_DARK}, ${WA})` : '#fff',
        color: active ? '#fff' : '#0f172a',
        borderRadius: 999,
        padding: '7px 13px',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        boxShadow: active ? '0 4px 12px rgba(37, 211, 102, 0.22)' : 'none',
      }}
    >
      {icon ? <i className={`fa ${icon}`} aria-hidden /> : null}
      {label}
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          opacity: active ? 0.9 : 0.7,
          background: active ? 'rgba(255,255,255,0.22)' : '#f1f5f9',
          borderRadius: 999,
          padding: '1px 7px',
          color: active ? '#fff' : '#64748b',
        }}
      >
        {count}
      </span>
    </button>
  )
}

export default WhatsAppTemplateManager
